import type { DailyRanksKeyword, KpsRollUp, MetricKey, MetricRollUp } from "@/types/rankTracker";
import type { KpsKeywordBreakdownViewModel, KpsRollUpBreakdownViewModel } from "@/components/keyword-bank/KpsBreakdownPopover";

/**
 * The keyword-arm view model, built from the wire's `kpsBreakdown` -- `null` exactly when there is
 * nothing to open (`kpsBreakdown` is `null` exactly when `kps` is `null`,
 * `components/rank-tracker.md § The KPS breakdown on the wire`). This is the one field the trigger's
 * presence is read from; no second predicate is derived from `kps` itself.
 * `decisions/the-kps-breakdown-renders-a-view-model.md`.
 *
 * Shared by every consumer of the KPS tile/cell -- `RankHeatmapTable`'s `KeywordRow` and
 * `KeywordRankDetailModal` both build the keyword arm from this one mapper
 * (moved here from `RankHeatmapTable/KeywordRow.tsx` so a third consumer would not have to import a
 * row component for its view-model function).
 */
export function buildKeywordKpsViewModel(keyword: DailyRanksKeyword): KpsKeywordBreakdownViewModel | null {
  if (keyword.kpsBreakdown === null) return null;

  return {
    level: "keyword",
    total: keyword.kps,
    label: keyword.keyword,
    factors: keyword.kpsBreakdown.factors,
    heroRelevanceFactor: keyword.kpsBreakdown.heroRelevanceFactor,
    includesCompetitorBrand: keyword.kpsBreakdown.includesCompetitorBrand,
    noOverlapWithHeroContent: keyword.kpsBreakdown.noOverlapWithHeroContent,
  };
}

/**
 * The roll-up-arm view model, shared by `ThemeRow`, `ProductRow` and `KeywordRankDetailModal` --
 * all three roll up the same wire shape (`metrics`, `kpsRollUp`) into the same arm
 * (`decisions/the-kps-breakdown-renders-a-view-model.md`), so this is the one place that mapping is
 * written. `null` exactly when `kpsRollUp` is `null` -- the one field the trigger's presence is read
 * from. `keywordCount` is **`metrics.kps.keywordCount`**, never the row's own `keywordCount`: the
 * response states that base once, on the metric roll-up
 * (`decisions/a-metric-roll-up-carries-its-own-base.md § The KPS base is stated once`).
 *
 * Moved here from `RankHeatmapTable/ThemeRow.tsx`, which `ProductRow.tsx` used to import it from --
 * a third consumer (the detail modal) made that borrowing untenable.
 */
export function buildKpsRollUpViewModel(
  label: string,
  metrics: Record<MetricKey, MetricRollUp>,
  kpsRollUp: KpsRollUp | null,
): KpsRollUpBreakdownViewModel | null {
  if (kpsRollUp === null) return null;

  return {
    level: "rollUp",
    total: metrics.kps.value,
    label,
    factors: kpsRollUp.factors,
    keywordCount: metrics.kps.keywordCount,
    penaltyKeywordCount: kpsRollUp.penaltyKeywordCount,
  };
}
